import UserApi from 'api/UserApi';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Container, TextField, Typography, CircularProgress, Paper } from '@mui/material';
import logoImage from '../styles/images/jeju_logo.png';
import env from 'config/env';

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [remember, setRemember] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const rememberedId = localStorage.getItem('rememberedId');
    if (rememberedId) {
      setUsername(rememberedId);
      setRemember(true);
    }
  }, []);

  React.useEffect(() => {
    if (remember) {
      localStorage.setItem('rememberedId', username);
    } else {
      localStorage.removeItem('rememberedId');
    }
  }, [remember, username]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // 로그인 API 호출 로직
      const response = await UserApi.IdLogin(username, password);
      // 로그인 성공 시 처리 로직
      alert('로그인 성공');
      navigate('/main');
    } catch (error: any) {
      console.log(error);
      // alert(error.response?.data?.message || '알 수 없는 오류');
    } finally {
      setIsLoading(false);
    }
  };

  const ENV = env();
  const department = `${ENV.DEPARTMENT}` || '우주모빌리티과';
  const phoneNumber = `${ENV.PHONE_NUMBER}` || '064-710-4826';
  const contactPerson = `${ENV.PERSON_NAME}` || '김하민 주무관';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.50',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: '20px',
        }}
      >
        <img src={logoImage} />
        <Typography
          sx={{
            color: '#434343',
            fontFamily: '"Noto Sans KR"',
            fontSize: '26px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
          }}
        >
          공간지능정보(Geo-AI) 융합 영상분석 행정서비스
        </Typography>
      </Box>

      <Typography
        sx={{
          color: '#454545',
          textAlign: 'center',
          fontFamily: 'Pretendard',
          fontSize: '48px',
          fontStyle: 'normal',
          fontWeight: 700,
          lineHeight: '140%',
          letterSpacing: '-0.96px',
          mb: '40px',
        }}
      >
        로그인
      </Typography>

      <Container
        component={Paper}
        sx={{
          width: '626px',
          padding: '56px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 3,
          boxShadow: 'none',
          border: '1px solid #E8E8E8',
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: '100%',
            maxWidth: '530px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <TextField
            margin="none"
            fullWidth
            id="username"
            label="ID"
            name="username"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            inputProps={{ maxLength: 30 }}
            sx={{
              '.MuiInputBase-formControl': {
                border: '1px solid #F0F0F0',
                borderRadius: 1,
                height: 'auto',
                py: 1,
              },
              '& .MuiInputBase-input': {
                border: '0px',
              },
              '& .MuiFormLabel-root': {
                color: '#8C8C8C',
                lineHeight: '1.9rem',
                fontWeight: 'light',
                px: 1,
                '&.MuiInputLabel-shrink': {
                  lineHeight: '3.9rem',
                  padding: '0px',
                },
              },
            }}
          />
          <TextField
            margin="none"
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            inputProps={{ maxLength: 300 }}
            sx={{
              '.MuiInputBase-formControl': {
                border: '1px solid #F0F0F0',
                borderRadius: 1,
                height: 'auto',
                py: 1,
              },
              '.MuiInputBase-input': {
                border: '0px',
              },
              '.MuiFormLabel-root': {
                color: '#8C8C8C',
                lineHeight: '1.9rem',
                fontWeight: 'light',
                px: 1,
                '&.MuiInputLabel-shrink': {
                  lineHeight: '3.9rem',
                  padding: '0px',
                },
              },
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              id="remember"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            <label
              htmlFor="remember"
              style={{
                fontSize: '16px',
                color: '#66686F',
                fontFamily: '"Noto Sans KR"',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '40px',
              }}
            >
              ID 기억하기
            </label>
          </Box>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading}
            sx={{
              minHeight: '68px',
              bgcolor: '#004CBD',
              color: 'var(--Color-Neutral-White, #FFF)',
              fontFamily: 'Pretendard',
              fontSize: '18px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '100%', // 18px
              letterSpacing: '-0.36px',
            }}
          >
            {isLoading ? <CircularProgress size={24} /> : '로그인'}
          </Button>
        </Box>
      </Container>
      <Typography
        variant="body2"
        align="center"
        sx={{
          mt: '40px',
          color: 'var(--Neutral-Gray600, #656565)',
          fontFamily: '"Noto Sans KR"',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '100%', // 14px
          letterSpacing: '-0.28px',
        }}
      >
        담당자: {department} {contactPerson} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; 전화번호: {phoneNumber}
      </Typography>
    </Box>
  );
};

export default LoginPage;
